import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SearchHistoryService {
  private readonly STORAGE_KEY = 'flowchest_search_history';
  private readonly MAX_HISTORY_ITEMS = 15;

  constructor() { }

  /**
   * Add a search term to history
   * Removes duplicates and keeps only the most recent searches
   */
  addToHistory(searchTerm: string): void {
    if (!searchTerm || !searchTerm.trim()) {
      return;
    }

    const trimmedTerm = searchTerm.trim();
    let history = this.getHistory();

    // Remove if already exists (to avoid duplicates)
    history = history.filter(item => item.toLowerCase() !== trimmedTerm.toLowerCase());

    // Add to beginning
    history.unshift(trimmedTerm);

    // Keep only the most recent items
    if (history.length > this.MAX_HISTORY_ITEMS) {
      history = history.slice(0, this.MAX_HISTORY_ITEMS);
    }

    // Save to localStorage
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  }

  /**
   * Get all search history
   */
  getHistory(): string[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const history = JSON.parse(stored);
        return Array.isArray(history) ? history : [];
      }
    } catch (error) {
      console.error('Error reading search history:', error);
    }
    return [];
  }

  /**
   * Filter history based on search input
   */
  getFilteredHistory(searchInput: string): string[] {
    if (!searchInput || !searchInput.trim()) {
      return this.getHistory();
    }

    const input = searchInput.trim().toLowerCase();
    return this.getHistory().filter(item => 
      item.toLowerCase().includes(input)
    );
  }

  /**
   * Remove a specific search term from history
   */
  removeFromHistory(searchTerm: string): void {
    const history = this.getHistory();
    const filtered = history.filter(item => item.toLowerCase() !== searchTerm.toLowerCase());
    
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error removing from search history:', error);
    }
  }

  /**
   * Clear all search history
   */
  clearHistory(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }

  /**
   * Check if history exists
   */
  hasHistory(): boolean {
    return this.getHistory().length > 0;
  }
}

